import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Heart, Clock, MapPin, Star, Share2, Info } from "lucide-react";
import { DishCard } from "@/components/customer/DishCard";
import { CustomizeSheet } from "@/components/customer/CustomizeSheet";
import { RatingPill, VegDot } from "@/components/common";
import { useCart } from "@/context/CartContext";
import { api, inr, apiError } from "@/lib/api";

export default function RestaurantDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem, count, subtotal } = useCart();
  const [r, setR] = useState(null);
  const [active, setActive] = useState("");
  const [vegOnly, setVegOnly] = useState(false);
  const [sheetItem, setSheetItem] = useState(null);
  const [faved, setFaved] = useState(false);

  useEffect(() => {
    api.get(`/restaurants/${id}`).then((res) => { setR(res.data); setActive(res.data.menu?.[0]?.category || ""); }).catch(() => {});
    api.get("/favorites").then((res) => setFaved(res.data.restaurants.some((x) => x.id === id))).catch(() => {});
  }, [id]);

  const addDirect = async (item) => {
    if (item.variants?.length || item.addons?.length || item.spice_levels?.length) { setSheetItem(item); return; }
    try {
      const res = await addItem(r, { menu_item_id: item.id, qty: 1, variant: null, addons: [], spice: null, instructions: "" });
      if (res?.ok) toast.success(`${item.name} added`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleFav = async () => {
    try { await api.post("/favorites/toggle", { type: "restaurant", id }); setFaved((f) => !f); } catch (e) { toast.error(apiError(e)); }
  };

  const share = () => { navigator.clipboard?.writeText(`${window.location.origin}/restaurant/${id}`); toast.success("Restaurant link copied"); };

  if (!r) return <div className="min-h-screen bg-surface max-w-[520px] lg:max-w-[900px] mx-auto"><div className="skel h-52" /><div className="p-4 space-y-3"><div className="skel h-6 w-2/3 rounded" /><div className="skel h-4 w-1/2 rounded" /></div></div>;

  const menu = r.menu.map((c) => ({ ...c, items: vegOnly ? c.items.filter((i) => i.veg) : c.items })).filter((c) => c.items.length);

  return (
    <div className="min-h-screen bg-surface max-w-[520px] lg:max-w-[900px] mx-auto pb-28">
      <div className="relative">
        <img src={r.cover} alt="" className="w-full h-52 lg:h-72 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-transparent" />
        <button onClick={() => nav(-1)} className="absolute top-4 left-4 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow tap" data-testid="back-btn"><ArrowLeft size={20} /></button>
        <div className="absolute top-4 right-4 flex gap-2">
          <button onClick={share} className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow tap"><Share2 size={17} /></button>
          <button onClick={toggleFav} className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow tap" data-testid="fav-restaurant"><Heart size={17} className={faved ? "fill-nonveg text-nonveg" : "text-ink-600"} /></button>
        </div>
      </div>

      <div className="px-4 -mt-8 relative">
        <div className="bg-white rounded-2xl border border-border p-4 shadow-sm">
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0">
              <h1 className="font-display font-extrabold text-xl text-ink">{r.name}</h1>
              <p className="text-sm text-ink-600 truncate">{r.cuisine}</p>
              <p className="text-xs text-ink-600 mt-0.5 flex items-center gap-1"><MapPin size={12} /> {r.address} · {r.distance_km} km</p>
            </div>
            <div className="flex flex-col items-center bg-veg text-white rounded-xl px-2.5 py-1.5">
              <span className="font-bold flex items-center gap-0.5">{r.rating}<Star size={12} className="fill-white" /></span>
              <span className="text-[10px]">{r.rating_count}+</span>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3 text-sm">
            <span className="flex items-center gap-1 text-ink font-semibold"><Clock size={14} className="text-coral" /> {r.delivery_time_min} min</span>
            <span className="text-ink-600">{r.delivery_fee === 0 ? "Free delivery" : `${inr(r.delivery_fee)} delivery`}</span>
            <span className={`ml-auto text-xs font-bold px-2 py-1 rounded-full ${r.is_open ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.is_open ? "Open" : "Closed"}</span>
          </div>
          {r.offer && <div className="mt-3 bg-coral-light text-coral rounded-xl px-3 py-2 text-sm font-bold flex items-center gap-2"><Info size={15} /> {r.offer}</div>}
          <p className="text-[11px] text-ink-600 mt-2">{r.hygiene} · {r.hours}</p>
        </div>
      </div>

      <div className="sticky top-0 z-30 bg-surface/95 glass px-4 py-2.5 mt-4 flex items-center gap-2 overflow-x-auto hide-scrollbar border-b border-border">
        <label className="flex items-center gap-1.5 shrink-0 mr-1">
          <span className="text-xs font-semibold text-ink-600">Veg</span>
          <input type="checkbox" checked={vegOnly} onChange={(e) => setVegOnly(e.target.checked)} className="accent-veg w-4 h-4" data-testid="veg-toggle" />
        </label>
        {menu.map((c) => (
          <button key={c.category} onClick={() => { setActive(c.category); document.getElementById(`cat-${c.category}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${active === c.category ? "bg-coral text-white border-coral" : "bg-white border-border text-ink-600"}`} data-testid={`menu-cat-${c.category}`}>{c.category}</button>
        ))}
      </div>

      <div className="px-4">
        {menu.map((c) => (
          <div key={c.category} id={`cat-${c.category}`} className="pt-4 scroll-mt-16">
            <h2 className="font-display font-bold text-lg text-ink">{c.category} <span className="text-ink-600 font-normal text-sm">({c.items.length})</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              {c.items.map((i) => <DishCard key={i.id} item={i} onAdd={addDirect} />)}
            </div>
          </div>
        ))}
        {r.reviews?.length > 0 && (
          <div className="pt-6">
            <h2 className="font-display font-bold text-lg text-ink mb-2">Reviews</h2>
            <div className="space-y-3">
              {r.reviews.slice(0, 6).map((rv) => (
                <div key={rv.id} className="bg-white rounded-xl border border-border p-3">
                  <div className="flex items-center justify-between"><span className="font-bold text-sm">{rv.customer_name}</span><RatingPill value={rv.restaurant_rating} /></div>
                  <p className="text-sm text-ink-600 mt-1">{rv.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <CustomizeSheet item={sheetItem} restaurant={r} open={!!sheetItem} onClose={() => setSheetItem(null)} onAdd={async (rest, it) => { try { const res = await addItem(rest, it); if (res?.ok) toast.success(`${it.name} added`); } catch (e) { toast.error(apiError(e)); } }} />

      {count > 0 && (
        <button onClick={() => nav("/cart")} className="fixed bottom-5 left-4 right-4 max-w-[488px] mx-auto z-40 bg-coral text-white rounded-2xl px-4 py-3.5 flex items-center justify-between shadow-lg tap" data-testid="view-cart-bar">
          <span className="font-bold">{count} item{count > 1 ? "s" : ""} · {inr(subtotal)}</span>
          <span className="font-bold">View Cart →</span>
        </button>
      )}
    </div>
  );
}
